import { useEffect, useRef, useState } from 'react';
import type { Symbol } from './instruments';
import { accumulateTicks, emptyBars, validTick } from './live-data';
import type { Tick, LiveBars } from './live-data';
import { DERIV_SYMBOLS, startDerivStream } from './deriv-stream';
import { validBidAsk } from './bid-ask';
import type { BidAsk } from './bid-ask';

type Live = { symbol?: Symbol; source?: string; priceType?: string; sessionOffsetMinutes?: number; candles: LiveBars; quote: Tick | null; book: BidAsk | null; checkedAt: number; error: boolean };
const empty: Live = { candles: emptyBars(), quote: null, book: null, checkedAt: 0, error: false };
export function useLiveQuotes(symbol: Symbol, busy: () => boolean) {
  const [live, setLive] = useState<Live>(empty);
  const busyRef = useRef(busy); busyRef.current = busy;
  useEffect(() => {
    // Headway's public VOL_80 quote summary is stale. Use its real candle
    // snapshots in CandleChart; no synthetic ticks or invented execution book.
    if (symbol === 'VOL40') {
      setLive({ ...empty, symbol, source: 'Vantage', priceType: 'candle', sessionOffsetMinutes: 180 });
      return;
    }
    if (symbol === 'VOL80') {
      setLive({ ...empty, symbol, source: 'Headway', priceType: 'candle' });
      return;
    }
    if (symbol === 'WTI') {
      setLive({ ...empty, symbol, source: 'Deriv', priceType: 'quote', error: true });
      return;
    }
    let disposed = false, pending = false, latest: Live = empty;
    let retryAt = 0, failures = 0;
    let controller: AbortController | null = null;
    setLive(empty);
    const accept = (value: any) => {
      const now = Date.now();
      if (value.symbol !== symbol || !Array.isArray(value.ticks) || !Number.isFinite(value.coverageFrom) ||
          (value.quote !== null && !validTick(value.quote, now))) throw new Error('Invalid live feed');
      if (disposed) return;
      const source = typeof value.source === 'string' ? value.source : 'Dukascopy';
      const priceType = value.priceType || 'bid', sessionOffsetMinutes = value.sessionOffsetMinutes ?? 0;
      const previous = latest.source === source && latest.priceType === priceType && latest.sessionOffsetMinutes === sessionOffsetMinutes ? latest : empty;
      const candles = accumulateTicks(previous.candles, value.ticks, value.coverageFrom, Date.parse(value.fetchedAt), now, sessionOffsetMinutes);
      const quote = value.quote && (!previous.quote || value.quote.time >= previous.quote.time) ? value.quote : previous.quote;
      const incomingBook = value.book ?? (priceType === 'bid' ? value.quote : null);
      const book = quote && quote === value.quote ? (validBidAsk(incomingBook, now) && incomingBook.time === quote.time ? incomingBook : null) : previous.book;
      latest = { symbol, source, priceType, sessionOffsetMinutes, candles, quote, book, checkedAt: now, error: false };
      retryAt = 0; failures = 0;
    };
    const canStream = typeof window.WebSocket === 'function' && Boolean(DERIV_SYMBOLS[symbol]);
    let streaming = canStream;
    const stopStream = canStream ? startDerivStream(symbol, value => { accept(value); streaming = true; }, () => {
      streaming = false;
      latest = { ...latest, symbol, checkedAt: Date.now(), error: true };
      // Drop the live badge immediately, even while the HTTP fallback waits.
      if (!disposed && !document.hidden && !busyRef.current()) setLive(latest);
    }) : () => {};
    const poll = async () => {
      if (disposed || document.hidden || busyRef.current()) return;
      if (canStream && streaming) {
        // Coalesce incoming ticks: never rerender in the middle of dragging.
        if (latest.symbol) setLive({ ...latest, checkedAt: Date.now() });
        return;
      }
      if (pending || Date.now() < retryAt) return;
      pending = true;
      controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 8000);
      try {
        const response = await fetch('/api/zone-quotes?symbol=' + symbol + '&since=' + (latest.quote?.time || 0), { signal: controller.signal });
        if (!response.ok) {
          const seconds = Number(response.headers?.get('Retry-After'));
          if (Number.isFinite(seconds) && seconds > 0) retryAt = Date.now() + Math.min(300, seconds) * 1000;
          const problem = await response.json().catch(() => null);
          if (typeof problem?.source === 'string' && problem.source !== latest.source) latest = { ...empty, source: problem.source };
          throw new Error('Live feed unavailable');
        }
        const value = await response.json();
        if (!streaming && !disposed) { accept(value); setLive(latest); }
      } catch {
        failures++;
        retryAt = Math.max(retryAt, Date.now() + Math.min(30000, 1000 * 2 ** Math.min(failures, 5)));
        if (!disposed && !streaming) { latest = { ...latest, symbol, checkedAt: Date.now(), error: true }; setLive(latest); }
      } finally { clearTimeout(timeout); pending = false; }
    };
    void poll();
    const timer = setInterval(() => void poll(), 1000);
    document.addEventListener('visibilitychange', poll);
    return () => { disposed = true; stopStream(); clearInterval(timer); controller?.abort(); document.removeEventListener('visibilitychange', poll); };
  }, [symbol]);
  return live.symbol === symbol ? live : empty;
}
